// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2024-09-19 13:28:45 +0200 (Thu, 19 Sep 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 145939 $
//  Purpose   : upload via webdav to help.vls.io
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Form, Button, Message, Input, Icon, List} from 'semantic-ui-react'
import now from 'performance-now'

// ---Framework imports
import log from 'uix/logging'
import {Actions} from 'uix/registries'
import {usePersistent, useState, useActions, useSelector, useApi, useNotify} from 'uix/hooks'
import {HeaderSegment, XMenu} from 'uix/components'
import {prettyBytes, formatDate} from 'uix/utils'

import {Differ, mimetype2options} from 'uix/components/editor'

// ---Local imports
import {MIMETYPES, MIME} from './utils'

// ---Globals
const FileDetails = ({name, size, author, modified, url}) => {
  return (
    <List horizontal relaxed>
      <List.Item>
        <List.Header>File</List.Header>
        {url ? (
          <a
            href={url}
            target="file_details"
            style={{color: size === undefined ? '#A44' : undefined}}
          >
            {url}
          </a>
        ) : (
          name
        )}
      </List.Item>
      {author && (
        <List.Item>
          <List.Header>Autor</List.Header>
          {author}
        </List.Item>
      )}
      {modified && (
        <List.Item>
          <List.Header>Änderungsdatum</List.Header>
          {formatDate(modified)}
        </List.Item>
      )}
      {size !== undefined && (
        <List.Item>
          <List.Header>Größe</List.Header>
          {prettyBytes(size)}
        </List.Item>
      )}
    </List>
  )
}

const WikiHubFileInfo = ({identifier, mimetype, changeTs}) => {
  let {data, error} = useApi(
    'getWikiHubInfo',
    {identifier, mimetype},
    {deps: changeTs, autoreset: false}
  )

  if (!data) {
    return null
  }
  return (
    <FileDetails
      name={data.filename}
      modified={data.version?.ts}
      author={data.version?.username}
      size={data.version?.size}
      url={data.url}
    />
  )
}

const DiffUpload = ({saveText, identifier, mimetype}) => {
  let {mode, options} = mimetype2options(mimetype)
  let {data, error} = useApi('getWikiHubInfo', {identifier, mimetype, loadtext: true})
  let leftDetails = data ? (
    <FileDetails
      name="on help.vls.io"
      modified={data.version?.ts}
      author={data.version?.username}
    />
  ) : null

  return (
    <HeaderSegment
      header="Review Changes"
      icon="exchange"
      flex
      style={{height: '70vh'}}
      portal="ReviewPortal"
    >
      <Differ
        flex
        headLeft={leftDetails}
        textLeft={data?.text}
        textMiddle={saveText}
        mode={mode.name}
        xml={options.xml}
        menuPortal="ReviewPortal"
      />
    </HeaderSegment>
  )
}
export const WebdavUpload = ({text, identifier, mimetype}) => {
  let notify = useNotify()
  let uploadWikiText = useActions(Actions.uploadWikiText)
  let callApi = useActions('callApi')

  let [infoActive, setInfoActive] = usePersistent('infoActive', false)
  let [username, setUsername] = usePersistent('webdavUser', '')
  let [changeTs, setChangeTs] = useState(null)
  let [password, setPassword] = useState('')
  let subheader = (
    <>
      Upload to{' '}
      <a href="https://help.vls.io" target="wiki_hub">
        help.vls.io
      </a>
    </>
  )

  let runUpload = () => {
    if (!password) return
    let uploadDone = (data, error) => {
      if (data) {
        if (data.success) {
          notify.success({
            header: 'Upload done',
            message: 'successfully saved to https://help.vls.io',
          })
        } else if (data.status_code == 401) {
          notify.warning({
            header: 'Authentification failed',
            dismissAfter: 5000,
            message: 'Either username or password is incorrect.',
          })
        } else {
          notify.warning({header: 'Upload failed', message: `code: ${data.status_code}`})
        }
        setChangeTs(now())
      }
      if (error) {
        notify.error({header: 'Upload error', error})
      }
    }
    let params = {text, identifier, username, password, mimetype}
    callApi('uploadWikiToHub', params, uploadDone, {method: 'post'})
  }

  let confirmCodeDiff = () => (
    <DiffUpload saveText={text} identifier={identifier} mimetype={mimetype} />
  )

  let menu = (
    <XMenu borderless>
      <XMenu.Item>
        <Icon name="user" />
        <Input
          size="mini"
          value={username}
          onChange={(event, data) => setUsername(data.value)}
          onKeyDown={event => (event.key == 'Enter' ? runUpload() : null)}
          placeholder="User"
          style={{width: '8em'}}
        />
      </XMenu.Item>
      <XMenu.Item>
        <Icon name="key" />
        <Input
          size="mini"
          value={password}
          onChange={(event, data) => setPassword(data.value)}
          onKeyDown={event => (event.key == 'Enter' ? runUpload() : null)}
          placeholder="Password"
          type="password"
          style={{width: '8em'}}
        >
          <input autoComplete="off" />
        </Input>
      </XMenu.Item>
      <XMenu.Item
        icon="save"
        disabled={!password}
        title="Upload"
        onClick={runUpload}
        confirm={{content: confirmCodeDiff, size: 'large', confirmButton: 'Upload'}}
      />
      <XMenu.Item icon="info" active={infoActive} onClick={() => setInfoActive(!infoActive)} />
    </XMenu>
  )
  return (
    <HeaderSegment icon="upload" header="WebDAV" subheader={subheader} menu={menu}>
      {infoActive && (
        <WikiHubFileInfo identifier={identifier} mimetype={mimetype} changeTs={changeTs} />
      )}
    </HeaderSegment>
  )
}
